import { queryOptions, UseMutationOptions } from "@tanstack/react-query";
import instance from "./axiosInstance";
import { Facility } from "types/facility.type";
import { TCreateFacility } from "pages/app/customer/AddFacility/createFacilitySchema";
import { TPatchFacility } from "pages/app/customer/AddFacility/patchFacilitySchema";

const FACILITY_ENDPOINT = "/facility";

export enum FacilityQueryKey {
  GetOne = "get-facility",
}

export const getFacility = (id: number) =>
  queryOptions({
    queryKey: [FacilityQueryKey.GetOne, id],
    queryFn: async (): Promise<Facility> =>
      (await instance.get(`${FACILITY_ENDPOINT}/${id}`)).data,
  });

export const createFacility = () => ({
  mutationFn: async (dto: TCreateFacility): Promise<Facility> =>
    (await instance.post(FACILITY_ENDPOINT, dto)).data,
});

export const patchFacility = (id: number) => ({
  mutationFn: async (dto: Partial<TPatchFacility>): Promise<Facility> =>
    (await instance.put(`${FACILITY_ENDPOINT}/${id}`, dto)).data,
});

export const deleteFacility = () => ({
  mutationFn: async (id: number): Promise<void> =>
    (await instance.delete(`${FACILITY_ENDPOINT}/${id}`)).data,
});

/**
 * Superuser tool: POST /facility/import-csv. Field name "file". The server
 * validates the file and answers at once; the rows are processed in the
 * background and a summary is emailed to a fixed address (summaryEmail).
 */
export type ImportFacilitiesCsvStarted = {
  rowCount: number;
  /** Where the server sends the summary when the import is done. */
  summaryEmail: string;
};

export const importFacilitiesCsv = async (
  file: File,
): Promise<ImportFacilitiesCsvStarted> => {
  const formData = new FormData();
  formData.append("file", file);
  return (
    await instance.post(`${FACILITY_ENDPOINT}/import-csv`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })
  ).data;
};

/**
 * Superuser tool: POST /facility/rename-all. Renames every facility in the
 * company to the generated scheme. The server answers at once; the renaming
 * runs in the background and a summary is emailed to a fixed address.
 */
export type RenameAllFacilitiesStarted = {
  facilityCount: number;
  summaryEmail: string;
};

export const renameAllFacilities =
  async (): Promise<RenameAllFacilitiesStarted> =>
    (await instance.post(`${FACILITY_ENDPOINT}/rename-all`)).data;

/**
 * Superuser tool: POST /facility/complete-data. Sets city/kommunKod from the
 * postal code and cfarNr from the customer's SCB arbetsställe on every
 * facility in the company. Background run, summary emailed to a fixed address.
 */
export type CompleteFacilityDataStarted = {
  facilityCount: number;
  summaryEmail: string;
};

export const completeFacilityData =
  async (): Promise<CompleteFacilityDataStarted> =>
    (await instance.post(`${FACILITY_ENDPOINT}/complete-data`)).data;
